import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ApplicationPersonDTO} from '../../../models/application/ApplicationPersonDTO';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {ApplicationDocumentFileService} from '../../../services/application-document-file.service';
import {take} from 'rxjs/operators';
import {SharedService} from '../../../../common/services/shared-service';
import {saveAs} from 'file-saver';
import {FileDTO} from '../../../../clm/models/FileDTO';
import {ApplicationDocumentDTO} from '../../../models/application/ApplicationDocumentDTO';
import {NameValueCodeDTO} from '../../../../common/models/NameValueCodeDTO';

interface DialogData {
  persons: ApplicationPersonDTO[];
  documentReferenceNumber?: string;
}

@Component({
  selector: 'app-application-person-upload-dialog',
  templateUrl: './application-document-upload-dialog.component.html',
  styleUrls: ['../../../../../../styles/global/_dialog-styles.scss'],
})
export class ApplicationDocumentUploadDialogComponent implements OnInit {
  form: FormGroup;
  persons: NameValueDTO[] = [];
  documentTypes: NameValueCodeDTO[] = [];
  fileNameOverride: string = null;
  isLoading = false;
  uploadedFile: FileDTO = null;
  applicationId: number;
  fileUploadConfig = {
    uploadedFileData: null,
    clearFileData: true,
    description: null
  };


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private formBuilder: FormBuilder,
    private sharedService: SharedService,
    private dialogRef: MatDialogRef<ApplicationDocumentUploadDialogComponent>,
    private applicationPersonFileUploadService: ApplicationDocumentFileService,
  ) {
  }

  ngOnInit(): void {
    this.initFormGroup();
    this.loadDocumentTypes();
    this.mapPersonsToNameValue();
    this.applicationId = this.data ? this.data['applicationId'] : null;
  }

  initFormGroup(): void {
    this.form = this.formBuilder.group({
      person: [null, Validators.required],
      documentType: [null, Validators.required]
    });
  }

  onPersonSelected(person: NameValueDTO): void {
    this.form.get('person').setValue(person);
    this.setFileNameOverride();
  }

  onDocumentTypeSelected(documentType: NameValueDTO): void {
    this.form.get('documentType').setValue(documentType);
    this.setFileNameOverride();
  }

  onFileUploadSuccess(uploadedFile: FileDTO): void {
    if (!uploadedFile) {
      return;
    }
    this.uploadedFile = uploadedFile;
    this.fileUploadConfig.uploadedFileData = uploadedFile;
  }

  onClickDownloadFile(): void {
    if (this.uploadedFile) {
      const {name, extension, file} = this.uploadedFile;
      const blobData = this.applicationPersonFileUploadService.base64toBlob(file, 'application/octet-stream');
      const blob = new Blob([blobData]);
      saveAs(blob, `${name}${extension}`);
    }
  }

  onClickCancel(): void {
    this.dialogRef.close();
  }

  onClickSubmit(): void {
    if (this.form.valid && this.uploadedFile) {
      const formValue = this.form.value;
      const person = this.data.persons.find(p => p.applicationPersonId === formValue.person.value);
      const documentTypeId = formValue.documentType.value;
      const documentTypeCode = this.documentTypes.find(type => type.value === documentTypeId)?.code;

      this.applicationPersonFileUploadService.linkDocumentToPerson(this.uploadedFile, this.applicationId, person.identification.idNumber, documentTypeCode)
        .pipe(take(1))
        .subscribe({
          next: (response: ApplicationDocumentDTO) => {
            this.sharedService.openSnackBar('Document added successfully');
            this.dialogRef.close({
              ...response,
              person
            });
          },
          error: (error) => {
            this.sharedService.openErrorDialog(error.message || 'Error uploading document');
          }
        });
    }
  }

  private loadDocumentTypes(): void {
    const fieldName = 'documentTypeCode';
    this.applicationPersonFileUploadService.getDocumentTypes(fieldName)
      .pipe(
        take(1),
      )
      .subscribe({
        next: (documentTypes: NameValueCodeDTO[]) => {
          this.documentTypes = documentTypes;
        },
        error: (error) => {
          console.error('Error loading document types:', error);
        }
      });
  }

  private mapPersonsToNameValue(): void {
    this.persons = this.data.persons.map(person => ({
      name: `${person.firstName} ${person.lastName}`,
      value: person.applicationPersonId
    }));
  }

  private setFileNameOverride(): void {
    const formValue = this.form.value;
    const person = this.data.persons.find(p => p.applicationPersonId === formValue.person.value);
    const documentType = this.documentTypes.find(d => d.value === formValue.documentType.value);
    this.fileNameOverride = `${documentType.name} - ${person.firstName} ${person.lastName}`;
  }
}
